import type { EventTrackingContext, GtmEventPayload, TrackableEvent } from './types';
import { installDataLayerGuard } from './dataLayerGuard';

const GTM_CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;

let activeContainerId: string | null = null;

export function normalizeGtmContainerId(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim().toUpperCase();
  if (!trimmed) {
    return null;
  }

  if (!GTM_CONTAINER_ID_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed;
}

export function buildEventTrackingContext(event: TrackableEvent): EventTrackingContext {
  return {
    eventSlug: event.slug || '',
    eventTitle: event.title,
  };
}

export function injectGtm(containerId: string): void {
  const normalized = normalizeGtmContainerId(containerId);
  if (!normalized) {
    return;
  }

  if (activeContainerId === normalized) {
    return;
  }

  removeGtm();
  activeContainerId = normalized;
  installDataLayerGuard();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${normalized}`;
  script.setAttribute('data-gtm-container', normalized);
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  noscript.setAttribute('data-gtm-noscript', normalized);
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${normalized}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

export function removeGtm(): void {
  if (!activeContainerId) {
    return;
  }

  const scripts = document.querySelectorAll(`script[data-gtm-container="${activeContainerId}"]`);
  scripts.forEach((script) => script.remove());

  const noscripts = document.querySelectorAll(`noscript[data-gtm-noscript="${activeContainerId}"]`);
  noscripts.forEach((noscript) => noscript.remove());

  activeContainerId = null;
}

export function pushGtmEvent(payload: GtmEventPayload): void {
  if (!activeContainerId) {
    return;
  }

  installDataLayerGuard();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  if (import.meta.env.DEV) {
    console.info('[GTM]', payload);
  }
}

export function trackPageView(context: EventTrackingContext, pagePath: string, pageTitle?: string): void {
  pushGtmEvent({
    ...context,
    event: 'page_view',
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
}

export function trackRegistrationSubmitted(
  event: TrackableEvent,
  data: { value: number; ticketType: string; isGroupRegistration: boolean; couponCode?: string },
): void {
  pushGtmEvent({
    ...buildEventTrackingContext(event),
    event: 'registration_submitted',
    value: data.value,
    currency: 'BRL',
    ticket_type: data.ticketType,
    is_group_registration: data.isGroupRegistration,
    coupon_code: data.couponCode,
  });
}

export function trackCouponApplied(event: TrackableEvent, couponCode: string, value: number): void {
  pushGtmEvent({
    ...buildEventTrackingContext(event),
    event: 'apply_coupon',
    coupon_code: couponCode,
    value,
    currency: 'BRL',
  });
}

export function trackPaymentMethodSelected(event: TrackableEvent, paymentMethod: string): void {
  pushGtmEvent({
    ...buildEventTrackingContext(event),
    event: 'select_payment_method',
    payment_method: paymentMethod,
  });
}

export function trackPaymentInitiated(
  event: TrackableEvent,
  paymentMethod: string,
  value?: number,
): void {
  pushGtmEvent({
    ...buildEventTrackingContext(event),
    event: 'payment_initiated',
    payment_method: paymentMethod,
    value,
    currency: 'BRL',
  });
}

export function trackPurchase(
  event: TrackableEvent,
  paymentMethod: string,
  value?: number,
  transactionId?: string,
): void {
  pushGtmEvent({
    ...buildEventTrackingContext(event),
    event: 'purchase',
    payment_method: paymentMethod,
    value,
    currency: 'BRL',
    transaction_id: transactionId,
  });
}

export function trackRegistrationComplete(
  event: TrackableEvent,
  paymentMethod?: string,
  value?: number,
  transactionId?: string,
): void {
  pushGtmEvent({
    ...buildEventTrackingContext(event),
    event: 'registration_complete',
    payment_method: paymentMethod,
    value,
    currency: 'BRL',
    transaction_id: transactionId,
  });
}
